import {
  CurrencyObject,
  DEFAULT_USER_LOCALE,
  getLocalesForVendorCountry,
  INTERNAL_CUSTOMER_ACCOUNT_DISPLAY_NAME,
  type LocaleSource,
  REGIONS_WITH_CURRENCY,
  REGIONS_WITH_LOCALES,
} from "@client-shared/lib/constants";
import * as api from "@client-shared/services/api";
import * as Sentry from "@sentry/react";
import * as T from "@shared/types";
import { TFunction } from "i18next";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { useSFDCAuthState } from "../../providers/auth/useSFDCAuthState";
import { handleDisplayErrorData } from "../../services/api/utilities/handleDisplayErrorData";
import {
  initialState,
  reducer,
  setCountries,
  setCurrencyObject as setCurrencyObjectAction,
  setCurrentLocale,
  setCustomerAccountCarrierDetails,
  setError,
  setInitialized,
  setLoading,
  setRegionLocales,
  setVendor,
} from "./actions";
import { LanguageOption, UseVendorUtils, VendorActions, VendorContextState, VendorState, vendorLocalStorageKeys } from "./types";
const defaultContextState: VendorContextState = [initialState, () => undefined];

export const VendorContext = React.createContext(defaultContextState);

type Props = {
  children: React.ReactNode;
  localeSource?: LocaleSource;
};

// Read the persisted vendor synchronously so currentVendor is already set on the first render. Restoring it in an effect is too late:
// descendants' effects (e.g. a <Navigate> redirect) run before the provider's own effects.
export const getInitialVendorState = (): VendorState => {
  try {
    const vendorObject = localStorage.getItem(vendorLocalStorageKeys.vendor);
    if (!vendorObject) {
      return initialState;
    }
    const currentVendor = JSON.parse(vendorObject) as T.Vendor | null;
    if (!currentVendor) {
      return initialState;
    }
    const currentLocale = localStorage.getItem(vendorLocalStorageKeys.currentLocale) as T.Locale | null;
    return { ...initialState, currentVendor, currentLocale: currentLocale ?? undefined, initialized: true };
  } catch {
    return initialState;
  }
};

export const VendorProvider: React.FC<Props> = ({ children, localeSource = "region" }: Props) => {
  const [authState] = useSFDCAuthState();
  const [vendorState, vendorDispatch] = React.useReducer(reducer, undefined, getInitialVendorState);
  const requestGenerationRef = React.useRef(0);
  const currentVendorRef = React.useRef(vendorState.currentVendor);
  React.useLayoutEffect(() => {
    currentVendorRef.current = vendorState.currentVendor;
  }, [vendorState.currentVendor]);
  const requestDispatch = React.useMemo(() => {
    const generation = requestGenerationRef.current;
    const vendorKey = vendorState.currentVendor?.vendorKey;
    return (action: VendorActions) => {
      if (generation === requestGenerationRef.current && vendorKey === currentVendorRef.current?.vendorKey) {
        vendorDispatch(action);
      }
    };
  }, [vendorState.currentVendor, vendorDispatch]);
  const { i18n } = useTranslation();
  const translations = React.useMemo(() => i18n.t, [i18n.t]);

  React.useEffect(() => {
    if (vendorState.currentVendor) {
      return;
    }
    const vendorObject = localStorage.getItem(vendorLocalStorageKeys.vendor);
    const currentLocaleValue = localStorage.getItem(vendorLocalStorageKeys.currentLocale);

    if (!vendorObject) {
      return;
    }

    const vendor = JSON.parse(vendorObject) as T.Vendor;
    const locale = currentLocaleValue as T.Locale;

    vendorDispatch(setVendor(vendor));
    if (currentLocaleValue) {
      vendorDispatch(setCurrentLocale(locale));
    }
    vendorDispatch(setInitialized(true));
  }, [vendorState.currentVendor, vendorDispatch]);

  React.useEffect(() => {
    if (!vendorState.initialized) {
      return;
    }
    if (!vendorState.currentVendor) {
      return;
    }
    Sentry.setUser({
      email: vendorState.currentVendor?.email ?? undefined,
      id: vendorState.currentVendor?.vendorKey,
      username: vendorState.currentVendor?.vendorName ?? undefined,
    });
  }, [vendorState.initialized, vendorState.currentVendor]);

  React.useEffect(() => {
    if (!vendorState.initialized || vendorState.regionLocales || !vendorState.currentVendor || !vendorState.currentLocale) {
      return;
    }
    vendorDispatch(setRegionLocales(getVendorLocales(vendorState.currentVendor, localeSource)));
  }, [
    localeSource,
    vendorState.currentLocale,
    vendorState.currentVendor,
    vendorState.initialized,
    vendorState.regionLocales,
    vendorDispatch,
  ]);

  React.useEffect(() => {
    if (!vendorState.initialized || !vendorState?.currentVendor?.region || vendorState?.currencyObject) {
      return;
    }
    const currencyObject: CurrencyObject = setCurrencyObjectFromVendor(vendorState.currentVendor);
    vendorDispatch(setCurrencyObjectAction(currencyObject));
  }, [
    vendorState.initialized,
    vendorState.currentVendor,
    vendorState.currentLocale,
    vendorState.currentVendor?.region,
    vendorState?.currencyObject,
    vendorDispatch,
  ]);

  React.useEffect(() => {
    if (
      !vendorState.initialized ||
      vendorState.currentLocale ||
      !vendorState.currentVendor ||
      !authState.sfdcAuthToken ||
      !vendorState.currencyObject
    ) {
      return;
    }
    void fetchLocale(
      vendorState.currentVendor,
      authState.sfdcAuthToken,
      Boolean(authState.isRoasterAdmin),
      localeSource,
      requestDispatch,
      translations
    );
  }, [
    authState.sfdcAuthToken,
    authState.isRoasterAdmin,
    localeSource,
    translations,
    vendorState.currentVendor,
    vendorState.initialized,
    vendorState.currentLocale,
    vendorState.currencyObject,
    requestDispatch,
  ]);

  React.useEffect(() => {
    if (!vendorState.initialized || !vendorState.currentLocale || !vendorState.currentVendor) {
      return;
    }
    const verifiedLocale = verifyVendorLocale(vendorState.currentVendor, vendorState.currentLocale, localeSource);
    if (verifiedLocale === vendorState.currentLocale) {
      return;
    }
    vendorDispatch(setCurrentLocale(verifiedLocale));
  }, [localeSource, vendorState.currentLocale, vendorState.currentVendor, vendorState.initialized, vendorDispatch]);

  React.useEffect(() => {
    if (!vendorState.initialized || !vendorState.currentLocale || !vendorState.currentVendor) {
      return;
    }
    void i18n.changeLanguage(vendorState.currentLocale);
  }, [i18n, vendorState, vendorState.currentVendor, vendorState.initialized, vendorState.currentLocale]);

  React.useEffect(() => {
    // Roaster admin has no vendorKey until they impersonate a vendor.
    // The /countries endpoint requires a vendorKey header for admin users (Lambda authorizer).
    // Skip until a vendor is selected to avoid a 403 → retry → logout loop.
    if (!vendorState.initialized || vendorState.countries || !vendorState.currentVendor || !authState.sfdcAuthToken) {
      return;
    }
    void fetchCountries(requestDispatch, translations);
  }, [
    vendorState.initialized,
    vendorState.countries,
    vendorState.currentVendor,
    authState.sfdcAuthToken,
    vendorDispatch,
    translations,
    requestDispatch,
  ]);

  React.useEffect(() => {
    if (
      !vendorState.initialized ||
      vendorState.customerAccountCarrierDetails ||
      !vendorState.currentVendor ||
      !authState.sfdcAuthToken ||
      !vendorState.currencyObject ||
      !vendorState.currentLocale ||
      !vendorState.currentVendor.region ||
      !vendorState.regionLocales
    ) {
      return;
    }
    void fetchCustomerAccountCarrierDetails(requestDispatch, translations);
  }, [
    authState.sfdcAuthToken,
    vendorState.currentVendor,
    vendorState.initialized,
    vendorState.customerAccountCarrierDetails,
    vendorState.currencyObject,
    vendorState.currentLocale,
    vendorState.regionLocales,
    translations,
    requestDispatch,
  ]);

  const updateProperties = React.useCallback(
    async (properties: T.VendorProperties) => {
      if (!vendorState.currentVendor) {
        return;
      }
      requestDispatch(setLoading(true));
      const isSuccess = await updateVendorProperties(properties, vendorState.currentVendor, requestDispatch, i18n.t);
      requestDispatch(setLoading(false));
      return isSuccess;
    },
    [i18n.t, vendorState.currentVendor, requestDispatch]
  );

  const clearVendorState = React.useCallback(() => {
    Object.values(vendorLocalStorageKeys).forEach((key) => localStorage.removeItem(key));
    Sentry.setUser(null);
    requestGenerationRef.current += 1;
    vendorDispatch({ type: "RESET_VENDOR" });
  }, [vendorDispatch]);

  const createSessionScope = React.useCallback(() => {
    const generation = requestGenerationRef.current;
    const isActive = () => generation === requestGenerationRef.current;
    return {
      isActive,
      dispatch: (action: VendorActions) => {
        if (isActive()) vendorDispatch(action);
      },
    };
  }, []);

  const getLanguageOptions = React.useCallback(
    () => getVendorLanguageOptions(vendorState.currentVendor, vendorState.regionLocales, i18n.t, localeSource),
    [localeSource, vendorState.currentVendor, vendorState.regionLocales, i18n.t]
  );

  const useVendorMethods: UseVendorUtils = React.useMemo(() => {
    return {
      updateProperties,
      setError: (error: Error) => vendorDispatch(setError(error)),
      clearVendorState,
      createSessionScope,
      getLanguageOptions,
    };
  }, [updateProperties, clearVendorState, createSessionScope, getLanguageOptions, vendorDispatch]);

  const contextValue: VendorContextState = React.useMemo(
    () => [vendorState, vendorDispatch, useVendorMethods],
    [vendorState, useVendorMethods, vendorDispatch]
  );
  return <VendorContext.Provider value={contextValue}>{children}</VendorContext.Provider>;
};

// utils
export const setCurrencyObjectFromVendor = (currentVendor: T.Vendor) => {
  return REGIONS_WITH_CURRENCY[currentVendor.region as keyof typeof REGIONS_WITH_CURRENCY];
};

const getLocaleObjectsForVendor = (vendor: T.Vendor, localeSource: LocaleSource): T.LocaleObject[] | undefined => {
  if (localeSource === "country") {
    const countryLocales = getLocalesForVendorCountry(vendor.countryCode);
    if (countryLocales) {
      return countryLocales;
    }
  }
  if (!vendor.region || !(vendor.region in REGIONS_WITH_LOCALES)) {
    return undefined;
  }
  return REGIONS_WITH_LOCALES[vendor.region as keyof typeof REGIONS_WITH_LOCALES];
};

export const verifyVendorLocale = (vendor: T.Vendor, locale?: T.Locale, localeSource: LocaleSource = "region"): T.Locale => {
  const localeObjects = getLocaleObjectsForVendor(vendor, localeSource);
  if (!localeObjects?.length) {
    return DEFAULT_USER_LOCALE;
  }
  const defaultLocale = localeObjects.find((obj) => obj.isDefault)?.locale ?? DEFAULT_USER_LOCALE;
  if (!locale) {
    return defaultLocale;
  }
  if (localeObjects.find((obj) => obj.locale === locale)) {
    return locale;
  }
  // Fall back to a language-prefix match so a bare "nl" on BDE resolves to nl_NL
  // instead of dropping to the region default (de_DE). Without this, an NL roaster
  // sitting on the BDE region renders in German even when their saved language is Dutch.
  const [requestedLanguage] = locale.split("_");
  return localeObjects.find((obj) => obj.locale.split("_")[0] === requestedLanguage)?.locale ?? defaultLocale;
};

export const getRegionLocales = (region: T.Vendor["region"]): T.Locale[] => {
  return (REGIONS_WITH_LOCALES[region as keyof typeof REGIONS_WITH_LOCALES] ?? []).map(
    (regionLocaleObject: T.RegionLocaleObject) => regionLocaleObject.locale
  );
};

export const getVendorLocales = (vendor: T.Vendor, localeSource: LocaleSource = "region"): T.Locale[] => {
  return getLocaleObjectsForVendor(vendor, localeSource)?.map((localeObject) => localeObject.locale) ?? [];
};

/**
 * Build region-derived language options for a locale picker. Primary path uses the
 * vendor's region (full label metadata); the fallback maps bare `regionLocales`
 * (restored from localStorage before the vendor resolves) back to their language
 * names. Labels are translated via `userNav.languages.*`; an unknown locale falls
 * back to rendering the raw locale string.
 */
export const getRegionLanguageOptions = (
  region: T.Vendor["region"] | undefined,
  regionLocales: T.Locale[] | undefined,
  t: TFunction<"translation", undefined>
): LanguageOption[] | undefined => {
  if (region && region in REGIONS_WITH_LOCALES) {
    return REGIONS_WITH_LOCALES[region as keyof typeof REGIONS_WITH_LOCALES].map((obj) => ({
      label: t(`userNav.languages.${obj.name}`),
      value: obj.locale,
    }));
  }
  if (!regionLocales?.length) {
    return undefined;
  }
  const localeNameMap = Object.values(REGIONS_WITH_LOCALES)
    .flat()
    .reduce<Map<string, string>>((map, obj) => {
      map.set(obj.locale, obj.name);
      return map;
    }, new Map());
  return regionLocales.map((locale) => {
    const name = localeNameMap.get(locale);
    return {
      label: name ? t(`userNav.languages.${name}`) : locale,
      value: locale,
    };
  });
};

export const getVendorLanguageOptions = (
  vendor: T.Vendor | undefined,
  regionLocales: T.Locale[] | undefined,
  t: TFunction<"translation", undefined>,
  localeSource: LocaleSource = "region"
): LanguageOption[] | undefined => {
  if (vendor) {
    const localeObjects = getLocaleObjectsForVendor(vendor, localeSource);
    if (localeObjects?.length) {
      return localeObjects.map((obj) => ({
        label: t(`userNav.languages.${obj.name}`),
        value: obj.locale,
      }));
    }
  }
  return getRegionLanguageOptions(vendor?.region, regionLocales, t);
};

export const getEnabledVendorCustomerAccountCarrierDetails = (customerAccountCarrierDetails: T.CarrierCustomerAccountBinding[]) => {
  if (!customerAccountCarrierDetails) {
    return [];
  }
  const groupedCustomerAccounts = customerAccountCarrierDetails.reduce<T.CarrierCustomerAccountBinding[]>((acc, detail) => {
    const { zenkraftShippingAccount, labelPrintEnabled, isManifestEnabled, accountType } = detail;

    const existingGroup = acc.find(
      (group) =>
        group.zenkraftShippingAccount === zenkraftShippingAccount &&
        group.labelPrintEnabled === labelPrintEnabled &&
        group.isManifestEnabled === isManifestEnabled
    );

    if (existingGroup) {
      existingGroup.customerAccountName = accountType;
      existingGroup.customerAccountNumber += `,${detail.customerAccountNumber}`;
      existingGroup.id = btoa(existingGroup.customerAccountNumber);
      existingGroup.groupedCustomerAccounts?.push({
        customerAccountDisplayName: detail.customerAccountDisplayName ?? detail.customerAccountName ?? accountType,
        customerAccountNumber: detail.customerAccountNumber,
        isBrevilleOwnedCarrierAccount: detail.isBrevilleOwnedCarrierAccount,
      });
      const isBrevilleOwnedCarrierAcccountGroup = existingGroup.groupedCustomerAccounts?.some(
        (groupedAccount) => groupedAccount.isBrevilleOwnedCarrierAccount
      );

      const baseDisplayName = isBrevilleOwnedCarrierAcccountGroup ? INTERNAL_CUSTOMER_ACCOUNT_DISPLAY_NAME : `${detail.accountType}`;

      // Only de-duplicate against groups on the same carrier — groups on different carriers are
      // already distinguishable by their carrier icon and should keep the shared base name.
      const existingNames = new Set(
        acc
          .filter((group) => group !== existingGroup && group.carrier === existingGroup.carrier)
          .map((group) => group.customerAccountDisplayName)
      );

      let uniqueName = baseDisplayName;
      let suffix = 0;

      while (existingNames.has(uniqueName)) {
        suffix++;
        uniqueName = `${baseDisplayName} - ${suffix}`;
      }

      existingGroup.customerAccountDisplayName = uniqueName;
    } else {
      const customerAccountDisplayName = detail.isBrevilleOwnedCarrierAccount
        ? INTERNAL_CUSTOMER_ACCOUNT_DISPLAY_NAME
        : (detail.customerAccountDisplayName ?? detail.customerAccountName ?? detail.accountType);
      acc.push({
        ...detail,
        customerAccountDisplayName,
        groupedCustomerAccounts: [
          {
            customerAccountDisplayName: detail.customerAccountDisplayName ?? detail.customerAccountName ?? accountType,
            customerAccountNumber: detail.customerAccountNumber,
            isBrevilleOwnedCarrierAccount: detail.isBrevilleOwnedCarrierAccount,
          },
        ],
        id: btoa(detail.customerAccountNumber),
      });
    }

    return acc;
  }, []);
  groupedCustomerAccounts.sort((a, b) => {
    const aIsBrevilleOwned = a.groupedCustomerAccounts?.some((account) => account.isBrevilleOwnedCarrierAccount) ?? false;
    const bIsBrevilleOwned = b.groupedCustomerAccounts?.some((account) => account.isBrevilleOwnedCarrierAccount) ?? false;
    return bIsBrevilleOwned ? 1 : aIsBrevilleOwned ? -1 : 0;
  });
  return groupedCustomerAccounts;
};

const fetchCountries = async (dispatch: (action: VendorActions) => void, t: TFunction<"translation", undefined>) => {
  try {
    const response = await api.getCountries();
    dispatch(setCountries(response.countries));
  } catch (e) {
    console.error(e);
    handleDisplayErrorData(t("apiErrorMessages.getCountries.message"), t("apiErrorMessages.getCountries.title"), e as Error);
  }
};

//sideEffects
const fetchLocale = async (
  currentVendor: T.Vendor,
  sfdcAuthToken: string,
  isRoasterAdmin: boolean,
  localeSource: LocaleSource,
  dispatch: (action: VendorActions) => void,
  t: TFunction<"translation", undefined>
) => {
  try {
    const userLocale = await resolveLocaleForCurrentSession(currentVendor, sfdcAuthToken, isRoasterAdmin);
    dispatch(setCurrentLocale(verifyVendorLocale(currentVendor, userLocale, localeSource)));
  } catch (e) {
    console.error(e);
    handleDisplayErrorData(t("apiErrorMessages.fetchUserLocale.message"), t("apiErrorMessages.fetchUserLocale.title"), e as Error);
  }
};

// When a roaster admin impersonates a vendor, the SFDC access token still belongs
// to the admin — so getUserLocale would return the admin's language. Fetch the
// impersonated user's saved LanguageLocaleKey instead so the UI reflects what the
// roaster has actually configured. Falls back to the admin's locale if userId is
// missing (older sessions before userId stamping shipped).
const resolveLocaleForCurrentSession = async (
  currentVendor: T.Vendor,
  sfdcAuthToken: string,
  isRoasterAdmin: boolean
): Promise<T.Locale | undefined> => {
  if (isRoasterAdmin && currentVendor.userId && currentVendor.vendorKey) {
    try {
      const profile = await api.getUserProfile({
        userId: currentVendor.userId,
        vendorKey: currentVendor.vendorKey,
      });
      const profileLocale = profile.languageLocaleKey as T.Locale | undefined;
      if (profileLocale) {
        return profileLocale;
      }
    } catch (e) {
      console.error("Failed to load impersonated user's locale; falling back to admin locale", e);
    }
  }
  return await api.getUserLocale(sfdcAuthToken);
};

const fetchCustomerAccountCarrierDetails = async (dispatch: (action: VendorActions) => void, t: TFunction<"translation", undefined>) => {
  try {
    const customerAccountCarrierDetails = await api.getCustomerAccountCarrierDetails();
    dispatch(setCustomerAccountCarrierDetails(getEnabledVendorCustomerAccountCarrierDetails(customerAccountCarrierDetails)));
  } catch (e) {
    console.error(e);
    handleDisplayErrorData(
      t("apiErrorMessages.getCustomerAccountCarrierDetails.message"),
      t("apiErrorMessages.getCustomerAccountCarrierDetails.title"),
      e as Error
    );
  }
};

const updateVendorProperties = async (
  properties: T.VendorProperties,
  currentVendor: T.Vendor,
  dispatch: (action: VendorActions) => void,
  t: TFunction<"translation", undefined>
) => {
  try {
    await api.setVendorProperties(properties);
    dispatch(setError(undefined));
    // toast.dark(t(message));
    dispatch(
      setVendor({
        ...currentVendor,
        properties: {
          ...currentVendor.properties,
          ...properties,
        },
      })
    );
    return true;
  } catch (e) {
    console.error(e);
    dispatch(setError(e as Error));
    handleDisplayErrorData("message", t("apiErrorMessages.updateVendorProperties.title"), e as Error);
    return false;
  }
};
